import { once, throttle } from 'lodash-es'
import { NEWSSTATE, ROUTESTATE } from '.'
import { MfcRouter, RouteConfig } from '../../types/state'
import { loadPMUserListHistory } from '../actions/message'
import { loadMissMFCRankings } from '../actions/missMFC'
import { workerArchive } from '../actions/workerActions'
import type { NewsSearchType } from '../libraries/mfc/MfcJsTypes'
import { getGalleryData } from '../mfcLogic/api/privateGallery'
import dynamicWithRetry, { loadWithRetry } from '../utilities/dynamicWithRetry'
import { safeDecodeUriComponent } from '../utilities/safeDecodeUriComponent'
import { slugToID } from './internal/users'
import { pubSubRouter } from './pubSub'
import { checkMail } from './state.mail'
import { getNews, NEWS_REFRESH_MS, search } from './state.news'
import { registerTimer } from './state.timers'

loadWithRetry(
    () => import('../components/presentation/Modals/FullscreenFloater'),
).catch(() => undefined)
const FullscreenFloater = dynamicWithRetry(
    () => import('../components/presentation/Modals/FullscreenFloater'),
)

export const PRELOAD_CACHE_TTL = 30000

export const routeConfigs = {
    get: (path: string, floater?: boolean) => {
        // console.log('[routeConfigs.get()]', { path })
        const route = routeConfigs.routes.find(
            (route) =>
                route.paths.includes(path) &&
                (floater !== undefined ? route.floater === floater : true),
        )
        if (!route) {
            // check if path can be matched to a slug
            const isSlug = !!slugToID[path]
            if (isSlug)
                return routeConfigs.routes.find(
                    (route) => route.paths.includes('[slug]'), // this is really naive but we don't do anything more than this
                )
            return routeConfigs.routes.find((route) =>
                route.paths.includes('error'),
            )
        }
        return route
    },
    routes: [
        {
            component: dynamicWithRetry(() => import('../pages/404')),
            paths: ['error'], // when is this used vs _error?
        },
        {
            component: dynamicWithRetry(() => import('../pages/miss-mfc')),
            paths: ['miss-mfc'],
            preload: loadMissMFCRankings,
            skipAwait: true,
        },
        {
            component: dynamicWithRetry(() => import('../pages/chat-archives')),
            paths: ['chat-archives'],
            preload: once(() => workerArchive(null)),
            // skip this during normal router loads as `handleChatArchivesRouting` calls it after awaiting modelslist
            // - if we do it here the socket is connected already so it should be safe
            skipRouterPreload: true,
        },
        {
            component: dynamicWithRetry(() => import('../pages/friends')),
            paths: ['friends'],
        },
        {
            component: dynamicWithRetry(() => import('../pages/index')),
            paths: ['', 'home'],
            default: true,
        },
        {
            component: dynamicWithRetry(() => import('../pages/mail')),
            paths: ['mail'],
            preload: throttle(checkMail, 60000),
        },
        {
            component: dynamicWithRetry(() => import('../pages/members')),
            paths: ['members'],
        },
        {
            component: dynamicWithRetry(() => import('../pages/messages')),
            paths: ['messages'],
            preload: once(loadPMUserListHistory),
            skipRouterPreload: true,
        },
        {
            component: dynamicWithRetry(() => import('../pages/model-browser')),
            paths: ['model-browser'],
            preload: () =>
                loadWithRetry(() => import('../actions/modelBrowser')).then(
                    (m) => m.preloadModelBrowser(),
                ),
            skipAwait: true,
        },
        {
            component: dynamicWithRetry(() => import('../pages/news')),
            paths: ['news'],
            preload: throttle(() => {
                if (AppRouter.route.visiblePage === 'news') {
                    const { query, type } = AppRouter.route.baseQuery
                    if (query) {
                        search((type as NewsSearchType) || 'news', query)
                        return
                    }
                }
                getNews({
                    update_only: 1,
                    silent: NEWSSTATE.news.newsItems.length > 0,
                })
            }, NEWS_REFRESH_MS),
        },
        {
            component: dynamicWithRetry(() => import('../pages/notes')),
            paths: ['notes'],
        },
        {
            component: dynamicWithRetry(
                () => import('../pages/private-gallery'),
            ),
            paths: ['private-gallery'],
            preload: once(getGalleryData),
            skipAwait: true,
        },
        {
            component: dynamicWithRetry(
                () => import('../pages/recommendations'),
            ),
            paths: ['recommendations'],
        },
        {
            component: dynamicWithRetry(() => import('../pages/rooms')),
            paths: ['rooms'],
        },
        {
            component: dynamicWithRetry(() => import('../pages/search')),
            paths: ['search', 'explore'],
            // the Explore view's Video Avatars shelf: its list starts loading
            // on the way there (a nav link's hover, the navigation itself)
            // rather than when the page mounts, which loads it too, as a
            // fallback. Not for a category's own view, which has no shelf
            preload: () =>
                loadWithRetry(() => import('../actions/videoAvatarFeed')).then(
                    (m) => {
                        if (!AppRouter.route.baseQuery?.view) {
                            void m.loadVideoAvatarFeed()
                        }
                    },
                ),
            skipAwait: true,
        },
        {
            component: FullscreenFloater,
            paths: ['room'],
            floater: true,
        },
        {
            component: FullscreenFloater,
            paths: ['message'],
            floater: true,
        },
        {
            component: FullscreenFloater,
            paths: ['profile', '[slug]'], // how do we match to this?
            floater: true,
        },
    ] as Array<RouteConfig>,
}

export const getRouteSegments = (
    fullRoute: string = '',
): [
    page: string,
    slug: string,
    surrogateSlug: string,
    query: { [key: string]: string },
    hash: string,
    fullRoute: string,
] => {
    const lowerFullRoute = fullRoute.toLowerCase()
    const [lowerDehashedFullRoute, hash] = lowerFullRoute.split('#')
    const [baseRoute, query] = lowerDehashedFullRoute.split('?')
    const [, route, slug = '', surrogateSlug = ''] = baseRoute.split('/')
    const compiledQuery = {}
    if (query) {
        const groups = query.split('&')
        groups.forEach((group) => {
            const [key, value] = group.split('=')
            if (key) {
                compiledQuery[safeDecodeUriComponent(key)] =
                    safeDecodeUriComponent(value || '')
            }
        })
    }
    // console.log('[getRouteSegment]', lowerFullRoute, {
    //     baseRoute,
    //     compiledQuery,
    // })

    return [route, slug, surrogateSlug, compiledQuery, hash, lowerFullRoute]
}

const setupCallbacks = (
    type: '_ready' | '_change' | '_completed',
    callback,
) => {
    AppRouter.events[type].push(callback)
    return () => {
        const index = AppRouter.events[type].findIndex((cb) => cb === callback)
        if (index >= 0) AppRouter.events[type].splice(index, 1)
    }
}

export const AppRouter: MfcRouter = {
    route: ROUTESTATE,
    init: (initialPath) => {
        const [page, slug, surrogateSlug, query, hash] =
            getRouteSegments(initialPath)
        const route = routeConfigs.routes.find((route) =>
            route.paths.includes(page),
        )
        const defaultRoute = routeConfigs.routes.find((route) => route.default)
        const foundRoute = route || defaultRoute
        if (!foundRoute.floater) {
            AppRouter.route.baseComponent = foundRoute.component
            AppRouter.route.basePage = foundRoute.paths[0]
            AppRouter.route.baseRoute = '/' + foundRoute.paths[0]
        } else {
            // ensure default route gets set as the base if we direct load a room/pm/profile
            AppRouter.route.baseComponent = defaultRoute.component
            AppRouter.route.basePage = defaultRoute.paths[0]
            AppRouter.route.baseRoute = '/' + defaultRoute.paths[0]
        }
        AppRouter.route.baseQuery = query
        AppRouter.route.visibleQuery = query
        AppRouter.route.hash = hash
        AppRouter.route.slug = slug
        AppRouter.route.surrogateSlug = surrogateSlug
        AppRouter.events.emit('ready', initialPath, false)
    },
    events: {
        emit: (event, data, immediate = true) =>
            pubSubRouter.emit(event, data, immediate),
        onReady: (callback) => setupCallbacks('_ready', callback),
        onChange: (callback) => setupCallbacks('_change', callback),
        onCompleted: (callback) => setupCallbacks('_completed', callback),
        offCompleted: (callback) => {
            const index = AppRouter.events._completed.findIndex(
                (cb) => cb === callback,
            )
            if (index >= 0) AppRouter.events._completed.splice(index, 1)
        },
        _ready: [],
        _change: [],
        _completed: [],
    },
}

pubSubRouter.on('ready', (initialPath) => {
    AppRouter.events._ready.forEach((callback) => callback(initialPath))
})
pubSubRouter.on('change', ({ routeInfo, initial }) => {
    AppRouter.events._change.forEach((callback) => callback(routeInfo, initial))
})
pubSubRouter.on('completed', ({ routeInfo, initial }) => {
    AppRouter.events._completed.forEach((callback) =>
        callback(routeInfo, initial),
    )
})

if (typeof window !== 'undefined') {
    registerTimer(
        'routeCache',
        () => {
            // check and clean up any stale cache
            const now = Date.now()
            AppRouter.route.preloadCache.forEach((cachedPreload, page) => {
                if (
                    cachedPreload &&
                    now - cachedPreload.timestamp >= PRELOAD_CACHE_TTL &&
                    page !== AppRouter.route.floatingPage &&
                    page !== AppRouter.route.basePage
                ) {
                    AppRouter.route.preloadCache.delete(page)
                }
            })
        },
        PRELOAD_CACHE_TTL,
    )
}
